






































import {Component, Prop, Ref, Vue, Watch} from 'vue-property-decorator'
import {Shot} from '@/store/shots'
import * as d3 from 'd3'
import {debounce} from 'lodash'
import {projectStore, shotStore} from '@/store'

interface TimeAxisOptions {
  axisWidth: number;
  margin: number;
  yPos: number;
}

@Component({
  name: 'VideoTrimmer',
  filters: {
    formatTime: (t: number): string => {
      return `${t.toFixed(2)}s`
    },
  },
})
export default class VideoTrimmer extends Vue {
  @Prop() shot!: Shot
  @Ref() svg!: SVGGElement
  @Ref() svgTotalTime!: SVGRect
  @Ref() svgTrimmerArea!: SVGRect
  @Ref() svgTrimmerIn!: SVGCircleElement
  @Ref() svgTrimmerOut!: SVGCircleElement
  @Ref() svgTimeAxis!: SVGGElement
  private trimmer: {
    width: number; height: number; xMargin: number; which: 'in' | 'out' | false; tickSize: number; tickCount: number;
  } = {
    width: 95,    // %
    height: 30,   // %
    xMargin: 2.5, // %
    which: false,
    tickSize: 15,
    tickCount: 15,
  }
  private prevPointerEvScreenX = 0

  mounted() {
    this.setupGraph()
  }

  destroyed() {
    window.removeEventListener('resize', this.setupGraph)
  }

  @Watch('shot')
  onShotChange(newVal: Shot, oldValue: Shot) {
    if (newVal !== oldValue)
      this.setupGraph()
  }

  private setupGraph() {
    try {
      this.renderTimeAxis()
    } catch(err) {
      console.error(`VideoTrimmer: error setting up graph: ${err}`)
    }
  }

  private renderTimeAxis() {
    const timeAxisEl = this.svgTimeAxis
    const options = this.timeAxisOptions()
    const scale = d3
      .scaleLinear()
      .domain([0, this.shot.durationInSec])
      .range([0, options.axisWidth])
    // const ticks = d3.ticks(0, this.shot.durationInSec, 20)
    const d3Call = d3
      .axisBottom(scale)
      .ticks(this.trimmer.tickCount)
      .tickSize(this.trimmer.tickSize)
    d3.select(timeAxisEl).call(d3Call as any)

    timeAxisEl.setAttribute('transform', `translate(${options.margin}, ${options.yPos})`)
  }

  timeAxisOptions(): TimeAxisOptions {
    const totalTimeEl = this.svgTotalTime as any
    const svgEl = this.svg as any
    return {
      axisWidth: this.svgTotalTimeWidthInPx(),
      margin: totalTimeEl.x.baseVal.value,
      // yPos: totalTimeEl.height.baseVal.value,
      yPos: svgEl.height.baseVal.value / 2,
    }
  }

  svgTotalTimeWidthInPx(): number {
    const totalTimeEl = this.svgTotalTime as any
    return totalTimeEl.width.baseVal.value
  }

  trimmerAreaWidthInPercent(): number {
    const totalTimeDuration = this.shot.durationInSec
    const shotTrimmedDuration = Shot.getTrimmedDurInSec(this.shot)
    const trimmedFraction = shotTrimmedDuration / (totalTimeDuration / 100)

    return trimmedFraction * (this.trimmer.width / 100)
  }

  inPointXInPercent(): number {
    const total = this.shot.durationInSec
    const inPoint = this.shot.inPoint
    const inPointFraction = inPoint / (total / 100)
    const result = inPointFraction * (this.trimmer.width / 100)

    return result + this.trimmer.xMargin
  }

  outPointXInPercent(): number {
    return this.inPointXInPercent() + this.trimmerAreaWidthInPercent()
  }

  startTrimming(ev: PointerEvent, which: 'in' | 'out') {
    this.trimmer.which = which
    if (which === 'in')
      this.svgTrimmerIn.setPointerCapture(ev.pointerId)
    else
      this.svgTrimmerOut.setPointerCapture(ev.pointerId)
  }

  moveTrimmer(ev: PointerEvent) {
    const trimmer = this.trimmer
    const movementX = ev.screenX - this.prevPointerEvScreenX
    if (trimmer.which && movementX !== 0) {
      const shot = this.shot
      const totalTime = shot.durationInSec
      const timeShiftInSec = movementX / (this.svgTotalTimeWidthInPx() / totalTime)
      let localTime

      if (trimmer.which === 'in') {
        let inPoint = shot.inPoint + timeShiftInSec
        if (inPoint <= 0)
          inPoint = 0
        else if (inPoint >= shot.outPoint)
          inPoint = shot.outPoint
        shotStore.setShotInPoint({shot, inPoint})
        localTime = inPoint
      } else {
        let outPoint = shot.outPoint + timeShiftInSec
        if (outPoint >= totalTime)
          outPoint = totalTime
        else if (outPoint <= shot.inPoint)
          outPoint = shot.inPoint
        shotStore.setShotOutPoint({shot, outPoint})
        localTime = outPoint
      }
      // TODO: this is needed to seek video, but triggers project save very frequently
      projectStore.setLocalTime(localTime)
    }
    this.prevPointerEvScreenX = ev.screenX
  }

  stopTrimming(ev: PointerEvent) {
    if (this.trimmer.which) {
      if (this.trimmer.which === 'in')
        this.svgTrimmerIn.releasePointerCapture(ev.pointerId)
      else
        this.svgTrimmerOut.releasePointerCapture(ev.pointerId)
      this.trimmer.which = false
      shotStore.requestSave({shot: this.shot, path: []})
    }
  }

  handleResize() {
    this.setupGraph()
  }
}
